import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    CONNECTION_ERROR_CODES,
    ModalClass,
    NOTIFICATION_STATUS_CODE,
    NotificationIdentifierModel,
    NotificationModel,
    NotificationService,
    NotificationUtilities,
    StartWorkService,
    StatusModel,
    TimesheetAndExpensesService,
    TimesheetEntryInterface,
    TimesheetEntryModel,
    TimesheetModel,
    TimeUtilities,
    TravelStatusUpdateModel,
    UserMasterService,
    YesNoModalDialogComponent,
    YesNoModalDialogModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { defer, EMPTY, iif, Observable, of, throwError } from 'rxjs';
import { catchError, map, mapTo, mergeMap, take, tap } from 'rxjs/operators';
import { OdataTravelStatusUpdateService } from './odata-travel-status-update.service';

@Injectable()
export class TravelStatusUpdateService {
    private readonly dateTimeFormat: string;
    public constructor(
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        private readonly notificationService: NotificationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly router: Router,
        private readonly appNotificationService: AppNotificationService,
        private readonly odataTravelStatusUpdateService: OdataTravelStatusUpdateService,
        private readonly startWorkService: StartWorkService,
        private readonly configService: ConfigService,
        private readonly userMasterService: UserMasterService,
    ) {
        this.dateTimeFormat = this.clientManager.getDateTimeFormat();
    }

    public submitStatus(travelStatusModel: TravelStatusUpdateModel): Observable<TravelStatusUpdateModel> {
        return this.startOfWorkDependency(travelStatusModel).pipe(
            mergeMap(() => this.setTravelBackTimesheetDependency(travelStatusModel)),
            mergeMap(() => this.checkForOtherNotificationsPause(travelStatusModel)),
            mergeMap(() => this.handleSkippedStatus(travelStatusModel)),
            mergeMap(() => this.odataSaveTravelStatusUpdate(travelStatusModel)),
            mergeMap(() => {
                this.log.debug(
                    `TravelStatusUpdateService: submitStatus: Status updated successfully to
                    ${travelStatusModel.nextStatus}`,
                );

                // save status in notification and localStorage
                const statusModel = new StatusModel(
                    travelStatusModel.nextStatus,
                    travelStatusModel.date.value,
                    travelStatusModel.endDate.value,
                );
                return this.notificationService.saveStatus(travelStatusModel.notification, statusModel);
            }),
            map(() => travelStatusModel),
        );
    }

    /**
     * start of work needs to be set in the P58 environment.
     * In AA countries, start of work is replaced with a pre-defined work schedule and as such always existing.
     * start of work is not required for PaceOnline58 and wherever timesheet is not enabled
     *
     * @param travelStatusModel the travel status model
     * @returns {Observable<void>} observable
     */
    private startOfWorkDependency(travelStatusModel: TravelStatusUpdateModel): Observable<void> {
        if (travelStatusModel.nextStatus === NOTIFICATION_STATUS_CODE.ETA) {
            travelStatusModel.notification.dispOrEtaStart = travelStatusModel.startMoment;
            travelStatusModel.notification.dispOrEtaEnd = travelStatusModel.endMoment;
        }

        if (
            this.isNextStatusTRAVorOSRorROSRorFCC(travelStatusModel) &&
            !this.userMasterService.isPaceOnlineUser() &&
            this.clientManager.isNgmP58() &&
            !this.configService.isTimesheetDisabled()
        ) {
            return this.setStartOfWorkForP58(travelStatusModel);
        }
        return of(undefined);
    }

    /**
     * This function sets start of work
     * If SoW is Invalid, set SoW timestamp same as timestap of TRAV or OSR(if user does OSR directy)
     * Should submit timesheet, if SoW is set for earlier day and Status is not TRAV
     *
     * @param travelStatusModel the travel status model
     * @returns the observable
     */
    private setStartOfWorkForP58(travelStatusModel: TravelStatusUpdateModel): Observable<void> {
        const startOfWork = this.timesheetAndExpensesService.getStartOfWork();
        const status = this.notificationService.getStatus(travelStatusModel.notification);

        if (!startOfWork.isValid()) {
            this.log.debug(
                `TravelStatusUpdateService: setStartOfWorkForP58: SoW was not set. Setting it same as travelStatusModel timestamp: ${travelStatusModel.startMoment.format(
                    this.dateTimeFormat,
                )}`,
            );
            return this.startWorkService.setStartOfWork(travelStatusModel.startMoment);
        } else if (
            startOfWork.isValid() &&
            !startOfWork.isSame(moment(), 'days') &&
            status.statusCode !== NOTIFICATION_STATUS_CODE.TRAV
        ) {
            this.log.debug(
                `TravelStatusUpdateService: setStartOfWorkForP58: SoW ${startOfWork.format(
                    this.dateTimeFormat,
                )} is on previous day, today is ${moment().toString()}, asking user to submit timesheet.`,
            );
            return this.submitPreviousTimesheet().pipe(
                mergeMap(() => throwError(`${ConfigConstants.TravelStatusErrors.TIMESHEET_PENDING_ERROR}`)),
            );
        }
        return this.updateStartOfWork(travelStatusModel, startOfWork);
    }

    /**
     * If start-of-work exists and is on an earlier day, we need to disable sending TRAV
     * and only allow OSR if the notification was already in TRAV.
     *
     * @returns the observable
     */
    private submitPreviousTimesheet(): Observable<void> {
        const timesheetModalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_TITLE'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_BODY'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_TIMESHEET_BUTTON'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_SOW_PROBLEM_OK_BUTTON'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return (timesheetModalInstance.content?.modalResult ?? of(false)).pipe(
            take(1),
            mergeMap((modalResult: boolean) => {
                if (modalResult) {
                    this.log.debug(
                        `TravelStatusUpdateService: submitPreviousTimesheet: asking user to submit timesheet, navigating to timesheet`,
                    );
                    return this.router.navigate([ConfigConstants.routeUrlTimesheet]);
                }
                return of(undefined);
            }),
            catchError((error) => {
                this.log.error(
                    `TravelStatusUpdateService: submitPreviousTimesheet: Error in submit timesheet before travel status update modal dialog:,
                     ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                return of(undefined);
            }),
            tap(() => {
                timesheetModalInstance.hide();
            }),
            mapTo(undefined),
        );
    }
    /**
     * Update start-of-work, if start time of TRAV/OSR is before current start-of-work and after previous end-of-work
     * If TRAV/OSR time is before previous end-of-work, it needs to be set correctly by user again
     *
     * @param travelStatusModel the travel status model
     * @param startOfWork the start of work
     * @returns the observable
     */
    private updateStartOfWork(
        travelStatusModel: TravelStatusUpdateModel,
        startOfWork: moment.Moment,
    ): Observable<void> {
        const submittedTimeSheet = this.timesheetAndExpensesService.getSubmittedTimesheet();
        const endOfWork = submittedTimeSheet.getEndOfWork();

        if (!endOfWork.isValid() || travelStatusModel.startMoment.isSameOrAfter(endOfWork)) {
            if (travelStatusModel.startMoment.isBefore(startOfWork)) {
                this.log.debug(
                    `TravelStatusUpdateService: updateStartOfWork: Updating start of work from ${startOfWork.format(
                        this.dateTimeFormat,
                    )} to ${travelStatusModel.startMoment.format(this.dateTimeFormat)}`,
                );
                return this.timesheetAndExpensesService
                    .saveStartOfWork(travelStatusModel.startMoment)
                    .pipe(mapTo(undefined));
            }
            return of(undefined);
        } else {
            const type = AppNotificationType.WARN;
            const title = <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_EOW_PROBLEM_TITLE');
            const body = <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_EOW_PROBLEM_BODY', {
                endOfWork: endOfWork.format(this.dateTimeFormat),
            });

            this.appNotificationService.setAppNotification(type, title, body);

            return throwError(
                `${ConfigConstants.TravelStatusErrors.INVALID_START_OF_WORK_ERROR} ${endOfWork.format(
                    this.dateTimeFormat,
                )}`,
            );
        }
    }

    /**
     * DISP -> ETA -> TRAV -> OSR
     * This function is to check if user skipped the status,
     * This returns true, if user has skipped status
     *
     * @param currentStatus the current status
     * @param nextStatus the next status
     * @returns true if status is skipped
     */
    public isStatusSkipped(currentStatus: NOTIFICATION_STATUS_CODE, nextStatus: NOTIFICATION_STATUS_CODE): boolean {
        return (
            (currentStatus === NOTIFICATION_STATUS_CODE.DISP && nextStatus !== NOTIFICATION_STATUS_CODE.ETA) ||
            (currentStatus === NOTIFICATION_STATUS_CODE.ETA &&
                nextStatus !== NOTIFICATION_STATUS_CODE.TRAV &&
                nextStatus !== NOTIFICATION_STATUS_CODE.ROSR &&
                nextStatus !== NOTIFICATION_STATUS_CODE.FCC)
        );
    }

    private checkForOtherNotificationsPause(
        travelStatusModel: TravelStatusUpdateModel,
    ): Observable<void[] | undefined> {
        if (!this.isNextStatusTRAVorOSRorROSRorFCC(travelStatusModel)) {
            return of(undefined);
        }
        const currentNotification = travelStatusModel.notification;
        const otherNotificationsInProgress = this.notificationService
            .getAllNotificationsInProgress()
            .filter(
                (notification) =>
                    !notification.isPaused &&
                    !NotificationIdentifierModel.compare(notification.identifier, currentNotification.identifier),
            );
        if (otherNotificationsInProgress.length === 0) {
            return of(undefined);
        }
        return this.showInterruptionModalInstance(otherNotificationsInProgress).pipe(
            mergeMap((result) => {
                if (result) {
                    return this.notificationService.savePausedIndicator(otherNotificationsInProgress, true);
                }
                return throwError(
                    'TravelStatusUpdateService: checkForOtherNotificationsPause: status change cancelled by user',
                );
            }),
        );
    }

    /**
     * PACE app allows user to skip the status
     * If user submits directly from DISP to TRAV, then ETA should be updated
     * If user submits directly from DISP to ROSR/FCC, then ETA should be updated
     * If user submits directly from DISP to OSR, then ETA and TRAV should be updated
     * If user submits directly from ETA to OSR, then TRAV should be updated
     */

    private handleSkippedStatus(travelStatusModel: TravelStatusUpdateModel): Observable<void> {
        return defer(() => {
            const currentStatus = this.notificationService.getStatus(travelStatusModel.notification);

            // this function is to check if the status is skipped, returns true
            if (!this.isStatusSkipped(currentStatus.statusCode, travelStatusModel.nextStatus)) {
                return of(undefined);
            }

            this.log.debug(
                `TravelStatusUpdateService: handlingStatus: Updating status from ${currentStatus.statusCode} to ${travelStatusModel.nextStatus} `,
            );

            return iif(
                () => currentStatus.statusCode === NOTIFICATION_STATUS_CODE.DISP,
                this.saveAndUpdateEtaModel(travelStatusModel).pipe(
                    mergeMap(() =>
                        travelStatusModel.nextStatus === NOTIFICATION_STATUS_CODE.OSR
                            ? this.saveAndUpdateTravModel(travelStatusModel)
                            : of(undefined),
                    ),
                ),
                this.saveAndUpdateTravModel(travelStatusModel),
            );
        });
    }

    /**
     * Create the ETA model and ETA status Model
     * Save and update the status in local storage and also to backend
     *
     * @param travelStatusModel the travel status model
     * @returns the observable for the status update
     */
    // @TODO was changed from private to public to make it testable. Please do refactor, if it stil should be private
    public saveAndUpdateEtaModel(travelStatusModel: TravelStatusUpdateModel): Observable<void> {
        return defer(() => {
            const etaModel = new TravelStatusUpdateModel(travelStatusModel.notification, NOTIFICATION_STATUS_CODE.ETA);
            const statusModel = new StatusModel(
                NOTIFICATION_STATUS_CODE.ETA,
                etaModel.date.value,
                etaModel.endDate.value,
            );

            travelStatusModel.notification.dispOrEtaStart = etaModel.startMoment.clone();
            travelStatusModel.notification.dispOrEtaEnd = etaModel.endMoment.clone();

            return this.odataTravelStatusUpdateService
                .saveStatus(etaModel)
                .pipe(mergeMap(() => this.notificationService.saveStatus(travelStatusModel.notification, statusModel)));
        });
    }

    /**
     * Create the TRAV model and TRAV status Model
     * Save and update the status in local storage and also to backend
     *
     * @param travelStatusModel the travel status model
     * @returns the observable for the status update
     */
    // @TODO was changed from private to public to make it testable. Please do refactor, if it stil should be private
    public saveAndUpdateTravModel(travelStatusModel: TravelStatusUpdateModel): Observable<void> {
        return defer(() => {
            const travModel = new TravelStatusUpdateModel(
                travelStatusModel.notification,
                NOTIFICATION_STATUS_CODE.TRAV,
            );

            this.changeStatusModelTimesForSkippingStatusChanges(travModel, travelStatusModel.startMoment);

            const statusModel = new StatusModel(
                NOTIFICATION_STATUS_CODE.TRAV,
                travModel.date.value,
                travModel.endDate.value,
            );

            return this.odataTravelStatusUpdateService
                .saveStatus(travModel)
                .pipe(mergeMap(() => this.notificationService.saveStatus(travelStatusModel.notification, statusModel)));
        });
    }

    /**
     * Check if the Last Travel back entry and current trav start time overlaps
     * If Overlaps, show popup to ask user if he want to adapt last travel back time of other notification
     *
     * @param travelStatusModel the travel status model
     * @returns the observable for the save process
     */
    private setTravelBackTimesheetDependency(travelStatusModel: TravelStatusUpdateModel): Observable<void> {
        if (
            this.isNextStatusTRAVorOSRorROSRorFCC(travelStatusModel) &&
            this.clientManager.isNgmP58OrSA() &&
            !this.userMasterService.isPaceOnlineUser()
        ) {
            const timesheet = this.timesheetAndExpensesService.getCurrentTimesheet();
            const timeSheetEntries = timesheet.getTimesheetEntries();

            const lastTravelBackEntry = timeSheetEntries.find(
                (entry) =>
                    TimesheetAndExpensesService.isTravelBackEntry(entry) &&
                    (<TimesheetEntryModel>entry).isTravelBackEditable,
            );

            if (
                lastTravelBackEntry &&
                TimeUtilities.hasOverlap(
                    travelStatusModel.time.value,
                    travelStatusModel.endTime.value,
                    lastTravelBackEntry.startTime.value,
                    lastTravelBackEntry.endTime.value,
                )
            ) {
                // If TRAV/OSR time overlaps with editable lastTravelBackEntry but is before the startTime of lastTravelBackEntry
                // then lastTravelBackEntry timestamp should not be adapted and user needs to set TRAV/OSR correctly again
                if (travelStatusModel.time.value.isBefore(lastTravelBackEntry.startTime.value)) {
                    const type = AppNotificationType.WARN;
                    const title = <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_OVERLAP_PROBLEM_TITLE');
                    const body = <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_OVERLAP_PROBLEM_BODY', {
                        startTime: lastTravelBackEntry.startTime.value.format(this.dateTimeFormat),
                    });

                    this.appNotificationService.setAppNotification(type, title, body);
                    this.log.debug(`TravelStatusUpdateService: setTravelBackTimesheetDependency: Timesheet overlaps`);
                    return throwError(() => `${ConfigConstants.TravelStatusErrors.TIMESHEET_OVERLAP_ERROR}`);
                }

                return this.adaptLastTravelBackTime(travelStatusModel, lastTravelBackEntry, timesheet).pipe(
                    mergeMap(() =>
                        throwError(
                            () => `${ConfigConstants.TravelStatusErrors.TIMESHEET_OVERLAP_ADAPT_LAST_TRBK_TIME}`,
                        ),
                    ),
                );
            }
        }
        return of(undefined);
    }

    private showInterruptionModalInstance(otherNotificationsInProgress: NotificationModel[]): Observable<boolean> {
        const interruptionModalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_TITLE'),
                    NotificationUtilities.getNotificationsFormattedWithNewLine(
                        otherNotificationsInProgress,
                        <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_BODY'),
                    ),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_OK_BUTTON'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_PAUSE_INFO_CANCEL_BUTTON'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
        return (interruptionModalInstance.content?.modalResult ?? of(false)).pipe(
            take(1),
            map((modalResult: boolean) => modalResult),
            tap(() => {
                interruptionModalInstance.hide();
            }),
        );
    }

    /**
     * This function shows popup to ask user if he really wants to start trav with that automatically “reduce” travelback of other notification if needed
     * If user clicks on Yes, then date and time of last travel back entry in timesheet is set to the TRAV/OSR status start time
     *
     * @param travelStatusModel the travel status model
     * @param lastTravelBackEntry the last travel back entry
     * @param timesheet the timesheet model
     * @returns the observable
     */
    private adaptLastTravelBackTime(
        travelStatusModel: TravelStatusUpdateModel,
        lastTravelBackEntry: TimesheetEntryInterface,
        timesheet: TimesheetModel,
    ): Observable<void> {
        const timesheetTRBKModalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new YesNoModalDialogModel(
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_TITLE'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_BODY'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_YES_BUTTON'),
                    <string>this.translate.instant('NOTIFICATION_STATUS_SUBMIT_UPDATE_TRBK_CANCEL_BUTTON'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });
        return (timesheetTRBKModalInstance.content?.modalResult.asObservable() ?? of()).pipe(
            take(1),
            mergeMap((modalResult: boolean) => {
                if (modalResult) {
                    this.log.debug(
                        `TravelStatusUpdateService: adaptLastTravelBackTime: Auto updating the end time of last travel back entry in timesheet to ${travelStatusModel.time.value.format(
                            this.dateTimeFormat,
                        )}`,
                    );
                    lastTravelBackEntry.endDate.value = travelStatusModel.time.value.clone();
                    lastTravelBackEntry.endTime.value = travelStatusModel.time.value.clone();
                    return this.timesheetAndExpensesService.saveCurrentTimesheet(timesheet).pipe(mapTo(undefined));
                }
                return of(undefined);
            }),
            tap(() => timesheetTRBKModalInstance.hide()),
            catchError((error) => {
                this.log.error(
                    `TravelStatusUpdateService: adaptLastTravelBackTime: Error in travel status update -> adapt TRBK dialog:,
                     ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                return of(undefined);
            }),
        );
    }

    private odataSaveTravelStatusUpdate(travelStatusModel: TravelStatusUpdateModel): Observable<unknown> {
        return this.odataTravelStatusUpdateService.saveStatus(travelStatusModel).pipe(
            catchError((error) => {
                this.log.error(
                    `TravelStatusUpdateService: odataSaveTravelStatusUpdate: ERROR ->
                                ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                        : <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                    error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                        ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                        : <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                );
                return EMPTY;
            }),
        );
    }

    // Returns true if next status is either TRAV or OSR or ROSR or FCC
    public isNextStatusTRAVorOSRorROSRorFCC(travelStatusModel: TravelStatusUpdateModel): boolean {
        return (
            travelStatusModel.nextStatus === NOTIFICATION_STATUS_CODE.TRAV ||
            travelStatusModel.nextStatus === NOTIFICATION_STATUS_CODE.OSR ||
            travelStatusModel.nextStatus === NOTIFICATION_STATUS_CODE.ROSR ||
            travelStatusModel.nextStatus === NOTIFICATION_STATUS_CODE.FCC
        );
    }

    public changeStatusModelTimesForSkippingStatusChanges(
        statusModel: TravelStatusUpdateModel,
        newMoment: moment.Moment,
    ): void {
        statusModel.date.value = newMoment.clone();
        statusModel.time.value = newMoment.clone();
        statusModel.endDate.value = newMoment.clone();
        statusModel.endTime.value = newMoment.clone();
        statusModel.startMoment = newMoment.clone();
        statusModel.endMoment = newMoment.clone();
    }
}
